import React, { useState, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";

export default function SSODomainVerification({ domainVerifications = {}, disabled, onUpdate }) {
    const { t } = useTranslation();

    const METHOD_OPTIONS = [
        { value: "dns_txt", label: t("ssoSettings.methodDnsTxt") },
        { value: "email", label: t("ssoSettings.methodEmail") },
        { value: "support_ticket", label: t("ssoSettings.methodSupportTicket") },
    ];
    const [newDomain, setNewDomain] = useState("");
    const [method, setMethod] = useState("dns_txt");
    const [domainMessages, setDomainMessages] = useState({});
    const [formError, setFormError] = useState("");

    const { call: initiate, isLoading: isInitiating } = useBluelagoon({ method: "initiateDomainVerification" });
    const { call: check, isLoading: isChecking } = useBluelagoon({ method: "checkDomainVerification" });
    const { call: remove, isLoading: isRemoving } = useBluelagoon({ method: "removeSSODomain" });

    const setDomainMsg = useCallback((domain, msg, type) => {
        setDomainMessages((prev) => ({ ...prev, [domain]: { msg, type } }));
    }, []);

    const handleAdd = useCallback(async (e) => {
        e.preventDefault();
        setFormError("");
        const domain = newDomain.trim().toLowerCase();
        if (!domain) return;
        try {
            const res = await initiate({ domain, method });
            if (res?.status === 200) {
                setNewDomain("");
                onUpdate?.();
            } else {
                setFormError(res?.error || t("ssoSettings.domainVerifyFailed"));
            }
        } catch (err) {
            setFormError(err?.message || t("ssoSettings.domainVerifyFailed"));
        }
    }, [newDomain, method, initiate, onUpdate]);

    const handleCheck = useCallback(async (domain) => {
        setDomainMessages((prev) => ({ ...prev, [domain]: undefined }));
        try {
            const res = await check({ domain });
            if (res?.verified) {
                setDomainMsg(domain, t("ssoSettings.domainVerified"), "success");
                onUpdate?.();
            } else {
                setDomainMsg(domain, res?.message || t("ssoSettings.domainNotVerified"), "warning");
                onUpdate?.();
            }
        } catch (err) {
            setDomainMsg(domain, err?.message || t("ssoSettings.domainCheckFailed"), "error");
        }
    }, [check, onUpdate, setDomainMsg]);

    const handleRemove = useCallback(async (domain) => {
        setDomainMessages((prev) => ({ ...prev, [domain]: undefined }));
        try {
            const res = await remove({ domain });
            if (res?.status === 200) {
                onUpdate?.();
            } else {
                setDomainMsg(domain, res?.error || t("ssoSettings.domainRemoveFailed"), "error");
            }
        } catch (err) {
            setDomainMsg(domain, err?.message || t("ssoSettings.domainRemoveFailed"), "error");
        }
    }, [remove, onUpdate, setDomainMsg]);

    const domains = Object.entries(domainVerifications);

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-sm font-medium text-gray-900">{t("ssoSettings.verifiedDomains")}</h3>
                <p className="text-xs text-gray-500 mt-1">{t("ssoSettings.verifiedDomainsDesc")}</p>
            </div>
            {domains.length > 0 && <DomainList domains={domains} disabled={disabled} isChecking={isChecking}
                isRemoving={isRemoving} onCheck={handleCheck} onRemove={handleRemove} messages={domainMessages} />}
            {!disabled && (
                <div className="space-y-2">
                    <form onSubmit={handleAdd} className="flex gap-2 items-end">
                        <div className="flex-1">
                            <input type="text" value={newDomain} onChange={(e) => { setNewDomain(e.target.value); setFormError(""); }}
                                placeholder="yourcompany.com" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                        </div>
                        <select value={method} onChange={(e) => setMethod(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm">
                            {METHOD_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                        <button type="submit" disabled={isInitiating || !newDomain.trim()}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                            {isInitiating ? t("ssoSettings.adding") : t("ssoSettings.addDomain")}
                        </button>
                    </form>
                    {formError && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-md">{formError}</p>}
                    <p className="text-xs text-gray-400">{t("ssoSettings.domainHint")}</p>
                </div>
            )}
        </div>
    );
}

function DomainList({ domains, disabled, isChecking, isRemoving, onCheck, onRemove, messages }) {
    const { t } = useTranslation();
    return (
        <ul className="space-y-2">
            {domains.map(([domain, info]) => {
                const msg = messages[domain];
                return (
                    <li key={domain} className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className={`inline-block w-2 h-2 rounded-full ${info.status === "verified" ? "bg-green-500" : "bg-yellow-500"}`} />
                                <span className="font-medium">{domain}</span>
                                <StatusText info={info} />
                            </div>
                            {!disabled && (
                                <div className="flex gap-2">
                                    {info.status === "pending" && info.method === "dns_txt" && (
                                        <button onClick={() => onCheck(domain)} disabled={isChecking}
                                            className="text-blue-600 hover:underline text-xs">{t("ssoSettings.check")}</button>
                                    )}
                                    <button onClick={() => onRemove(domain)} disabled={isRemoving}
                                        className="text-red-600 hover:underline text-xs">{t("ssoSettings.remove")}</button>
                                </div>
                            )}
                        </div>
                        {msg && <InlineMessage message={msg.msg} type={msg.type} />}
                    </li>
                );
            })}
        </ul>
    );
}

function InlineMessage({ message, type }) {
    const styles = {
        error: "text-red-600 bg-red-50",
        warning: "text-yellow-700 bg-yellow-50",
        success: "text-green-600 bg-green-50",
    };
    return <p className={`text-xs px-2 py-1.5 rounded ${styles[type] || styles.warning}`}>{message}</p>;
}

function StatusText({ info }) {
    const { t } = useTranslation();
    if (info.status === "verified") return <span className="text-green-600 text-xs">{t("ssoSettings.statusVerified")}</span>;
    if (info.method === "dns_txt") return <span className="text-yellow-600 text-xs">{t("ssoSettings.statusDnsTxt", { code: info.code })}</span>;
    if (info.method === "email") return <span className="text-yellow-600 text-xs">{t("ssoSettings.statusEmail")}</span>;
    if (info.method === "support_ticket") return <span className="text-yellow-600 text-xs">{t("ssoSettings.statusTicket", { ticketId: info.ticketId })}</span>;
    return <span className="text-yellow-600 text-xs">{t("ssoSettings.statusPending")}</span>;
}
